import { InAppNotification, InAppNotificationType } from '@interfaces';
import { NotificationRegistry } from './notifications.registry.types';

type ForeignerNotificationType =
  | InAppNotificationType.EMPLOYEE_ADDED_DOCUMENT_COMMENT
  | InAppNotificationType.APPLICATION_STATUS_CHANGED
  | InAppNotificationType.APPLICATION_STATUS_CHANGED_WITH_TYPE
  | InAppNotificationType.DOCUMENTS_LIST_CHANGED
  | InAppNotificationType.EMPLOYEE_SENT_MESSAGE_IN_CHAT
  | InAppNotificationType.EMPLOYEE_CANCELLED_VOIVODESHIP_OFFICE_APPOINTMENT
  | InAppNotificationType.APPLICATION_MODE_CHANGED
  | InAppNotificationType.NEW_FINAL_VERIFICATION_COMMENTS
  | InAppNotificationType.CHANGED_FINAL_VERIFICATION_COMMENTS
  | InAppNotificationType.NEW_APPLICATION_HAS_BEEN_ADDED
  | InAppNotificationType.NEW_APPLICATION_HAS_BEEN_ADDED_WITH_TYPE
  | InAppNotificationType.APPLICATION_HAS_BEEN_DELETED
  | InAppNotificationType.CONSULTANT_CREATED_APPOINTMENT
  | InAppNotificationType.CONSULTANT_ACCEPTED_APPOINTMENT_DATE
  | InAppNotificationType.CONSULTANT_REJECTED_APPOINTMENT_DATE
  | InAppNotificationType.CONSULTANT_CANCELLED_APPOINTMENT
  | InAppNotificationType.CONSULTANT_UPDATED_APPOINTMENT
  | InAppNotificationType.CONSULTANT_UPLOADED_DECISION
  | InAppNotificationType.WORK_PERMIT_LINKED_TO_ACCOUNT
  | InAppNotificationType.WORK_PERMIT_DOCUMENTS_LIST_CHANGED
  | InAppNotificationType.WORK_PERMIT_DOCUMENT_APPROVED
  | InAppNotificationType.WORK_PERMIT_DOCUMENT_REJECTED
  | InAppNotificationType.WORK_PERMIT_STATUS_CHANGED;

const getMyWorkPermitRoute = (notification: InAppNotification) => ({
  linkParams: ['my-work-permit', notification.relatedWorkPermitId],
});

const getUserProcessRoute = (notification: InAppNotification, suffix?: string[]) => ({
  linkParams: ['user-process', notification.relatedUserProcessId, ...(suffix || [])],
});

export const foreignerNotificationRegistry: NotificationRegistry<ForeignerNotificationType> = {
  [InAppNotificationType.EMPLOYEE_ADDED_DOCUMENT_COMMENT]: {
    recipientGroup: 'foreigner',
    actionKey: 'route-only',
    icons: [{ name: 'mms', className: 'employee-sent-message-in-chat-icon' }],
    getRoute: notification => ({
      linkParams: ['user-process', notification.relatedUserProcessId, 'documents'],
      extras: {
        queryParams: {
          revealDocumentId: notification.relatedUserDocumentId,
          openChatForUserDocumentId: notification.relatedUserDocumentId,
        },
      },
    }),
  },
  [InAppNotificationType.APPLICATION_STATUS_CHANGED]: {
    recipientGroup: 'foreigner',
    actionKey: 'user-process-route-or-reload',
    icons: [{ name: 'difference', className: 'application-status-changed-icon' }],
    getRoute: notification => ({ ...getUserProcessRoute(notification), extras: { queryParams: {} } }),
  },
  [InAppNotificationType.APPLICATION_STATUS_CHANGED_WITH_TYPE]: {
    recipientGroup: 'foreigner',
    actionKey: 'user-process-route-or-reload',
    icons: [{ name: 'difference', className: 'application-status-changed-icon' }],
    getRoute: notification => ({ ...getUserProcessRoute(notification), extras: { queryParams: {} } }),
  },
  [InAppNotificationType.DOCUMENTS_LIST_CHANGED]: {
    recipientGroup: 'foreigner',
    actionKey: 'user-process-documents-refresh',
    icons: [{ name: 'edit_document', className: 'documents-list-changed-icon' }],
    getRoute: notification => ({ ...getUserProcessRoute(notification), extras: { queryParams: {} } }),
  },
  [InAppNotificationType.EMPLOYEE_SENT_MESSAGE_IN_CHAT]: {
    recipientGroup: 'foreigner',
    actionKey: 'user-chat',
    icons: [{ name: 'forum', className: 'new-chat-message-icon' }],
    getRoute: () => null,
  },
  [InAppNotificationType.EMPLOYEE_CANCELLED_VOIVODESHIP_OFFICE_APPOINTMENT]: {
    recipientGroup: 'foreigner',
    actionKey: 'user-process-route-or-reload',
    icons: [{ name: 'event_busy_outline', className: 'appointment-cancelled-icon' }],
    getRoute: notification => ({ ...getUserProcessRoute(notification), extras: { queryParams: {} } }),
  },

  [InAppNotificationType.APPLICATION_MODE_CHANGED]: {
    recipientGroup: 'foreigner',
    actionKey: 'user-process-route-or-reload',
    icons: [{ name: 'integration_instructions', className: 'sending-error-icon', fontSet: 'material-icons-outlined' }],
    getRoute: notification => ({ ...getUserProcessRoute(notification), extras: { queryParams: {} } }),
  },
  [InAppNotificationType.NEW_FINAL_VERIFICATION_COMMENTS]: {
    recipientGroup: 'foreigner',
    actionKey: 'user-process-summary-comments-route-or-reload',
    icons: [{ name: 'speaker_notes', className: 'action-icon', fontSet: 'material-icons-outlined' }],
    getRoute: notification => ({
      linkParams: ['user-process', notification.relatedUserProcessId, 'summary'],
      extras: { queryParams: { showDocumentsVerificationComment: true } },
    }),
  },
  [InAppNotificationType.CHANGED_FINAL_VERIFICATION_COMMENTS]: {
    recipientGroup: 'foreigner',
    actionKey: 'user-process-summary-comments-route-or-reload',
    icons: [{ name: 'speaker_notes', className: 'action-icon', fontSet: 'material-icons-outlined' }],
    getRoute: notification => ({
      linkParams: ['user-process', notification.relatedUserProcessId, 'summary'],
      extras: { queryParams: { showDocumentsVerificationComment: true } },
    }),
  },
  [InAppNotificationType.NEW_APPLICATION_HAS_BEEN_ADDED]: {
    recipientGroup: 'foreigner',
    actionKey: 'hard-user-process',
    icons: [{ name: 'post_add', className: 'new-application-has-been-added-icon', fontSet: 'material-icons-outlined' }],
    getRoute: notification => getUserProcessRoute(notification),
  },
  [InAppNotificationType.NEW_APPLICATION_HAS_BEEN_ADDED_WITH_TYPE]: {
    recipientGroup: 'foreigner',
    actionKey: 'hard-user-process',
    icons: [{ name: 'post_add', className: 'new-application-has-been-added-icon', fontSet: 'material-icons-outlined' }],
    getRoute: notification => getUserProcessRoute(notification),
  },
  [InAppNotificationType.APPLICATION_HAS_BEEN_DELETED]: {
    recipientGroup: 'foreigner',
    actionKey: 'noop',
    icons: [{ name: 'delete', className: 'application-has-been-removed', fontSet: 'material-icons-outlined' }],
    getRoute: () => null,
  },
  [InAppNotificationType.CONSULTANT_CREATED_APPOINTMENT]: {
    recipientGroup: 'foreigner',
    actionKey: 'user-process-route-or-reload',
    icons: [{ name: 'event_available', className: 'positive-icon' }],
    getRoute: notification => ({ ...getUserProcessRoute(notification), extras: { queryParams: {} } }),
  },
  [InAppNotificationType.CONSULTANT_ACCEPTED_APPOINTMENT_DATE]: {
    recipientGroup: 'foreigner',
    actionKey: 'user-process-route-or-reload',
    icons: [{ name: 'event_available', className: 'positive-icon' }],
    getRoute: notification => ({ ...getUserProcessRoute(notification), extras: { queryParams: {} } }),
  },
  [InAppNotificationType.CONSULTANT_REJECTED_APPOINTMENT_DATE]: {
    recipientGroup: 'foreigner',
    actionKey: 'user-process-route-or-reload',
    icons: [{ name: 'event_busy_outline', className: 'negative-icon' }],
    getRoute: notification => ({ ...getUserProcessRoute(notification), extras: { queryParams: {} } }),
  },
  [InAppNotificationType.CONSULTANT_CANCELLED_APPOINTMENT]: {
    recipientGroup: 'foreigner',
    actionKey: 'user-process-route-or-reload',
    icons: [{ name: 'event_busy_outline', className: 'negative-icon' }],
    getRoute: notification => ({ ...getUserProcessRoute(notification), extras: { queryParams: {} } }),
  },
  [InAppNotificationType.CONSULTANT_UPDATED_APPOINTMENT]: {
    recipientGroup: 'foreigner',
    actionKey: 'user-process-route-or-reload',
    icons: [{ name: 'breaking_news', fontSet: 'material-symbols-outlined', className: 'important-icon' }],
    getRoute: notification => ({ ...getUserProcessRoute(notification), extras: { queryParams: {} } }),
  },
  [InAppNotificationType.CONSULTANT_UPLOADED_DECISION]: {
    recipientGroup: 'foreigner',
    actionKey: 'user-process-route-or-reload',
    icons: [{ name: 'breaking_news', fontSet: 'material-symbols-outlined', className: 'important-icon' }],
    getRoute: notification => ({ ...getUserProcessRoute(notification), extras: { queryParams: {} } }),
  },
  [InAppNotificationType.WORK_PERMIT_LINKED_TO_ACCOUNT]: {
    recipientGroup: 'foreigner',
    actionKey: 'my-work-permit-refresh',
    autoReadOn: ['my-work-permit'],
    icons: [{ name: 'work', className: 'new-application-has-been-added-icon', fontSet: 'material-icons-outlined' }],
    getRoute: notification => getMyWorkPermitRoute(notification),
  },
  [InAppNotificationType.WORK_PERMIT_DOCUMENTS_LIST_CHANGED]: {
    recipientGroup: 'foreigner',
    actionKey: 'my-work-permit-refresh',
    autoReadOn: ['my-work-permit'],
    icons: [{ name: 'edit_document', className: 'documents-list-changed-icon' }],
    getRoute: notification => getMyWorkPermitRoute(notification),
  },
  [InAppNotificationType.WORK_PERMIT_DOCUMENT_APPROVED]: {
    recipientGroup: 'foreigner',
    actionKey: 'my-work-permit-refresh',
    autoReadOn: ['my-work-permit'],
    icons: [{ name: 'task_alt', className: 'positive-icon' }],
    getRoute: notification => getMyWorkPermitRoute(notification),
  },
  [InAppNotificationType.WORK_PERMIT_DOCUMENT_REJECTED]: {
    recipientGroup: 'foreigner',
    actionKey: 'my-work-permit-refresh',
    autoReadOn: ['my-work-permit'],
    icons: [{ name: 'report', className: 'negative-icon', fontSet: 'material-icons-outlined' }],
    getRoute: notification => getMyWorkPermitRoute(notification),
  },
  [InAppNotificationType.WORK_PERMIT_STATUS_CHANGED]: {
    recipientGroup: 'foreigner',
    actionKey: 'my-work-permit-refresh',
    autoReadOn: ['my-work-permit'],
    icons: [{ name: 'difference', className: 'application-status-changed-icon' }],
    getRoute: notification => getMyWorkPermitRoute(notification),
  },
};
